import { useState } from "react";

import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { useIsTruncated } from "@/hooks/useIsTruncated";
import { cn } from "@/lib/utils";

type TruncatedTooltipProps = {
    text: string;
    className?: string;
    tooltipSide?: "top" | "bottom" | "left" | "right";
};

/**
 * Renders `text` as a single line with ellipsis truncation, and shows a
 * tooltip with the full text on hover only when the text is actually
 * clipped. Shared between the centered chat header title and thread-list
 * row titles.
 *
 * Open is controlled by our own `pointerenter` / `pointerleave` handlers
 * instead of base-ui's built-in hover detection. base-ui watches
 * `pointerover` under the hood, which browsers also fire when an
 * element moves *under* a stationary pointer — collapsing the thread
 * sidebar pushed a random row under the cursor and conjured a tooltip
 * with no user intent. `pointerenter` is movement-driven (not layout-
 * driven), so the tooltip can only open when the user genuinely moves
 * their cursor onto the trigger.
 */
export function TruncatedTooltip({
    text,
    className,
    tooltipSide = "bottom",
}: TruncatedTooltipProps) {
    const { ref, isTruncated } = useIsTruncated(text);
    const [hovered, setHovered] = useState(false);
    const open = isTruncated && hovered;

    return (
        <Tooltip open={open} onOpenChange={() => {}}>
            <TooltipTrigger
                render={
                    <span
                        ref={ref}
                        className={cn("block truncate", className)}
                        onPointerEnter={() => setHovered(true)}
                        onPointerLeave={() => setHovered(false)}
                    />
                }
            >
                {text}
            </TooltipTrigger>
            {isTruncated ? (
                <TooltipContent side={tooltipSide}>{text}</TooltipContent>
            ) : null}
        </Tooltip>
    );
}
